<template>
    <Modal name="BULK_CANCEL_MODAL" width="380" :title="$t('appointment.cancelModal.title')" ref="cancelAppointments" actions>
    <template v-slot:body>
      <v-alert class="orange lighten-5 mb-6">

        <!-- canceled -->
        <div v-if="appointmentsToCancel.length >= 1">
          <p class="body-2 orange--text mb-2">
            {{ $t('appointment.cancelModal.appointmentsToCancel') }}
          </p>
          <v-chip v-for="(item, index) in appointmentsToCancel" :key="index" class="mr-1 mb-1 orange" x-small>
            <span class="white--text">
              {{ item.code }}
            </span>
          </v-chip>
        </div>

        <v-divider v-if="hasCancelledAndRemovedAppointments" class="mt-5 mb-5 orange lighten-4" />

        <!-- removed -->
        <div v-if="appointmentsToRemove.length >= 1">
          <p class="body-2 orange--text mb-2">
            {{ $t('appointment.cancelModal.appointmentsToRemove') }}
          </p>
          <v-chip v-for="(item, index) in appointmentsToRemove" :key="index" class="mr-1 mb-1 orange" x-small>
            <span class="white--text">
              {{ item.code }}
            </span>
          </v-chip>
        </div>
      </v-alert>

      <v-select
        @focus="getCancelReasons"
        v-model="cancelReasonId"
        :label="$t('appointment.cancelModal.cancelReason')"
        :loading="cancelReasonsLoading"
        :items="cancelReasons"
        :no-data-text="cancelReasonsLoading ? 'Loading...' : 'No data available'"
        item-text="description"
        item-value="id"
        outlined
      />

      <v-textarea
        v-model="cancelReason"
        :hint="$t('rules.minValue', { value: 10 })"
        :rules="[rules.minLength]"
        outlined
      />

      <div class="mt-2 d-flex align-center">
        <HCheckbox
          class="mr-2"
          v-model="notifyPropertyManager"
          :label="$t('appointment.cancelModal.notifyPropertyManager')"
        />
        <InformationTooltip :message="$t('appointment.cancelModal.notifyPropertyManagerInfoMessage')" />
      </div>

    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="loading"
        elevation="0"
        color="grey"
        text
      >
        {{ $t('appointment.buttons.cancel') }}
      </v-btn>
      <v-btn
        @click="cancelAppointments"
        :loading="loading"
        :disabled="!cancelReasonId || !cancelReason"
        class="ml-5 warning"
        elevation="0"
      >
        {{ $t('appointment.buttons.confirm') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { handleErrorMessage, minLength } from '@/util/errorHandling'
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { api } from '@/services'

import Modal from '../../app/Modal.vue'
import HCheckbox from '../../app/HCheckbox.vue'
import InformationTooltip from '../../app/InformationTooltip.vue'

export default {
  name: 'ModalBulkCancel',

  components: {
    Modal,
    HCheckbox,
    InformationTooltip
  },

  props: {
    selectedAppointments: {
      type: Array,
      required: true,
    },

    category: {
      type: String,
      required: false,
    },
  },

  computed: {
    ...mapGetters(['cancelReasons']),

    hasCancelledAndRemovedAppointments() {
      return this.appointmentsToCancel.length > 0 && this.appointmentsToRemove.length > 0
    },

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN') || userRoles?.includes('ROLE_PARTNER')
    },
  },

  methods: {
    ...mapActions(['bulkCancelAppointments', 'bulkCancelAppointments', 'fetchCancelReasons', 'showSuccessMessage', 'showErrorMessage']),
    ...mapMutations(['closeAllModals']),

    resetFields() {
      this.cancelReasonId = null
      this.cancelReason = null
    },

    createPayload() {
      return {
        appointment_ids: this.selectedAppointments.map(appointment => appointment.id),
        cancel_request: {
          reason_cancel_id: this.cancelReasonId,
          cancel_reason: this.cancelReason,
        },
        notify_property_manager: this.notifyPropertyManager,
      }
    },

    async cancelAppointments() {
      this.loading = true

      const userPath = this.isAdminOrPartner ? 'admin' : 'web'

      try {
        await api.post(`${userPath}/appointmentGroup/cancelAppointments`, this.createPayload())
        this.showSuccessMessage('Appointments cancelled with success')
        this.$emit('refreshMap')
        this.resetFields()
        this.closeAllModals()
      } catch ({ data }) {
        this.showErrorMessage(handleErrorMessage(data))
      } finally {
        this.loading = false
      }
    },

    getCancelReasons() {
      if (this.cancelReasons.length > 0) return

      this.cancelReasonsLoading = true
      this.fetchCancelReasons(this.isAdminOrPartner).finally(() => this.cancelReasonsLoading = false)
    },

    setAppointmentsToCancelOrRemove() {
      const selectedIds = this.selectedAppointments.map(a => a.id)

      this.appointmentsToRemove = this.appointmentsToRemove.filter(appointment => selectedIds.includes(appointment.id))
      this.appointmentsToCancel = this.appointmentsToCancel.filter(appointment => selectedIds.includes(appointment.id))

      this.selectedAppointments.map((appointment) => {
        if (appointment?.group_id) {
          if (!this.appointmentsToRemove.some(appoint => appoint.id === appointment.id)) {
            this.appointmentsToRemove.push(appointment)
          }
        } else {
          if (!this.appointmentsToCancel.some(appoint => appoint.id === appointment.id)) {
            this.appointmentsToCancel.push(appointment)
          }
        }
      })
    }
  },

  watch: {
    selectedAppointments() {
      this.setAppointmentsToCancelOrRemove()
    },

    category() {
      this.appointmentsToCancel = []
      this.appointmentsToRemove = []
    },
  },

  data() {
    return {
      loading: false,
      cancelReason: null,
      cancelReasonId: null,
      cancelReasonsLoading: false,
      notifyPropertyManager: false,

      appointmentsToCancel: [],
      appointmentsToRemove: [],

      rules: {
        minLength,
      }
    }
  },

  mounted() {
    this.$watch(() => this.$refs.cancelAppointments.dialog, (isModalOpen) => {
      if (isModalOpen) {
        this.resetFields()
      }
    })
  }
}
</script>
